<template>
  <div id="instagram-section">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <h2 v-html="section.title" />
        </div>
      </div>
      <div class="row">
        <div class="col-4">
          <div
            class="instagram-embed"
            v-html="section.embed_1"
          />
        </div>
        <div class="col-4">
          <div
            class="instagram-embed"
            v-html="section.embed_2"
          />
        </div>
        <div class="col-4">
          <div
            class="instagram-embed"
            v-html="section.embed_3"
          />
        </div>
      </div>
      <div class="row button-holder">
        <a
          :href="section.link.url"
          :target="section.link.target"
          class="link btn"
        >
          {{ section.link.title }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import RysEmbedModal from '@/components/modals/EmbedModal.vue';
import stripBlockTags from '@/utils/strip-block-tags';

export default {
  name: 'SplitImageSection',

  props: {
    section: { type: Object, required: true }
  },

  setup() {
    const modal = useModal();
    return { modal };
  },

  data() {
    return {
      showSlider: false,
      screenWidth: 0
    };
  },

  computed: {
    btnClass() {
      if (this.section.button_text) {
        if (this.section.style === 'fancy') {
          return 'black';
        }

        if (this.section.bg_color === 'white') {
          return 'ghost';
        }
      }

      return '';
    },

    imgXPos() {
      if (this.screenWidth >= 1170) {
        return this.section.img_x_pos;
      }
      return this.section.mob_img_x_pos;
    },

    imgYPos() {
      if (this.screenWidth >= 1170) {
        return this.section.img_y_pos;
      }
      return this.section.mob_img_y_pos;
    }
  },

  mounted() {
    window.addEventListener('resize', this.onWindowResize);
    this.onWindowResize();
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  },

  methods: {
    stripBlockTags,

    showEmbedModal(html) {
      this.modal(RysEmbedModal, this, { props: { embed: html } });
    },

    onLinkClick(e, section) {
      if (section.link_shows_video && section.video) {
        e.preventDefault();
        this.showEmbedModal(section.video);
      }
    },

    onWindowResize() {
      this.screenWidth = window.innerWidth;
    }
  }
};
</script>

<style lang="scss" scoped>
@use 'sass:math';

#instagram-section {
  padding: 25px 0;

  @media screen and (min-width: $breakpoint-lg) {
    padding: 25px 0;
  }

  .container h2 {
    margin-bottom: 40px;
  }

  .button-holder {
    justify-content: center;
    margin-top: 20px;
  }

  h2::v-deep {
    font-size: 64px;
    line-height: 64px;
    color: $thd-black;
    margin: 0;
    text-transform: uppercase;

    strong {
      color: $thd-orange;
      font-weight: 700;
    }
  }

  .sub-text {
    margin-bottom: 40px;
    font-size: 20px;
  }

  img {
    width: 100%;
    margin: 0 0 40px;
    cursor: pointer;
  }
}

div.split-image-section {
  position: relative;
  overflow-x: clip;

  .bg {
    display: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;

    .orange {
      position: relative;
      background-color: $thd-orange;
      height: 100%;
      width: 100%;
      transform: rotateZ(2deg) scale(110%);
      z-index: 2;

      @media screen and (min-width: $breakpoint-lg) {
        width: 50%;
        transform: rotateZ(2deg) scale(105%);
      }
    }

    .img {
      height: 100%;
      padding-top: 0;
      position: absolute;
      right: 0;
      width: 50%;
      z-index: 1;

      img {
        position: absolute;
        top: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
      }
    }
  }

  .container {
    position: relative;
    z-index: 2;
    display: grid;
    gap: 24px;
    grid-template-columns: minmax(auto, 100%);
    grid-template-rows: repeat(4, minmax(0, auto));

    @media screen and (min-width: $breakpoint-lg) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-template-rows: repeat(2, auto) minmax(0, 1fr);
      grid-auto-flow: column dense;
      gap: 24px 60px;
    }

    .image-container,
    .video-container {
      @media screen and (min-width: $breakpoint-lg) {
        order: 9999;
        grid-row: span 3 / span 3;

        &.dir-left {
          order: -9999;
        }
      }

      .aspect {
        position: relative;
        width: 100%;
        padding-top: math.div(9, 16) * 100%;

        @media screen and (min-width: $breakpoint-lg) {
          padding-top: 100%;
        }

        > img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;

          &.clickable {
            cursor: pointer;
          }
        }
      }

      .embed::v-deep {
        width: 100%;
        height: 0;
        overflow: hidden;
        position: relative;
        padding-top: math.div(9, 16) * 100%;

        iframe {
          width: 100% !important;
          height: 100% !important;
          position: absolute;
          top: 0;
          left: 0;
        }
      }
    }
  }

  &.bg-white {
    background-color: $thd-white;

    h2 {
      color: $thd-black;
    }

    .content {
      color: $thd-dark-gray;
    }
  }

  &.bg-black {
    background-color: $thd-black;

    h2 {
      color: $thd-white;
    }

    .content {
      color: $thd-white;
    }
  }

  &.style-fancy {
    margin-top: 64px;

    @media screen and (min-width: $breakpoint-lg) {
      margin-top: 0;
    }

    .bg {
      display: flex;

      &.dir-left {
        flex-direction: row-reverse;

        .orange {
          transform: rotateZ(-2deg) scale(110%);

          @media screen and (min-width: $breakpoint-lg) {
            transform: rotateZ(-2deg) scale(105%);
          }
        }

        .img {
          left: 0;
          right: unset;
        }
      }
    }

    .container::v-deep {
      padding-top: 32px;

      @media screen and (min-width: $breakpoint-lg) {
        padding-top: 60px;
      }

      h2 {
        color: $thd-white;
      }

      .content {
        color: $thd-white;

        strong,
        b {
          color: $thd-black;
        }
      }

      .image-container {
        @media screen and (min-width: $breakpoint-lg) {
          visibility: hidden;
        }
      }
    }
  }
}

#compare {
  // background-color: #efefef;

  .container {
    padding-bottom: 60px;
    margin-top: 60px;

    @media screen and (min-width: $breakpoint-md) {
      padding-bottom: 50px;
      margin-top: 0;
    }
  }

  h2::v-deep {
    font-size: 64px;
    line-height: 64px;
    color: $thd-black;
    margin: 0 0 40px;
    text-transform: uppercase;

    strong {
      color: $thd-orange;
      font-weight: 700;
    }
  }

  .sub-text {
    margin-bottom: 40px;
    font-size: 20px;
  }

  .gallery-holder {
    display: grid;
    gap: 5px;

    @media screen and (min-width: $breakpoint-md) {
      gap: 10px;
      grid-template-columns: auto auto auto;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      cursor: pointer;
    }
  }
}

.yel-class {
  display: block !important;
}
</style>
